import { useEffect, useRef, useCallback, useState } from 'react';
import apiClient from '../services/apiClient';

// Cumulative per-visit checkpoints make retries idempotent. Hidden time is excluded.
export default function useWritingTimer(examId, running, paused = false) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const checkpoint = useRef(() => Promise.resolve());
  const timer = useRef(null);
  const active = Boolean(running && !paused);
  const activeRef = useRef(active);
  useEffect(() => {
    if (!examId) return undefined;
    const timerId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let elapsedMs = 0;
    let enabled = activeRef.current;
    let since = document.hidden || !enabled ? null : performance.now();
    const sample = () => {
      const now = performance.now();
      if (since !== null) elapsedMs += now - since;
      since = document.hidden || !enabled ? null : now;
      setElapsedSeconds(Math.floor(elapsedMs / 1000));
    };
    const flush = () => {
      sample();
      return Promise.resolve(apiClient.recordWritingTime(examId, { timerId, elapsedMs: Math.floor(elapsedMs) })).catch(() => {});
    };
    setElapsedSeconds(0);
    timer.current = { setActive: next => {
      sample();
      enabled = next;
      since = document.hidden || !enabled ? null : performance.now();
      void flush();
    } };
    checkpoint.current = flush;
    const onVisibility = () => { void flush(); };
    document.addEventListener('visibilitychange', onVisibility);
    const displayInterval = setInterval(sample, 1000);
    const interval = setInterval(flush, 15000);
    return () => {
      clearInterval(displayInterval);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      void flush();
      checkpoint.current = () => Promise.resolve();
      timer.current = null;
    };
  }, [examId]);
  useEffect(() => {
    activeRef.current = active;
    timer.current?.setActive(active);
  }, [active]);
  const flushTime = useCallback(() => checkpoint.current(), []);
  return { elapsedSeconds, flushTime };
}
